// Changes to apply, the half of the top bar's one control (ChangesAndApplies,
// #2336) on every page of a site (#1811): whether Design holds changes no apply
// has carried, with the way to the work list that approves them in its panel,
// and, after an approval, what it sent until the devices it reached settle.
//
// **One reader of the work list, and nothing counted here.** The word and the
// count are `changesToApplyOf`'s, beside `designOf` in `pages/devices/
// family.ts`, over the same `useWorkListRead` the device pages read, so the
// shell and a device's Design line cannot disagree. The click goes to Design ›
// Changes, where each plan is read before the one confirmation approves it; the
// shell holds no apply of its own and sends nothing.
//
// **When it reads.** Once when the page opens at a site, again when the
// operator moves to another page, and again after the operator's own write
// (`api/writes.ts`). Never on a timer, and no change stream is held for it, so
// another operator's change shows on the next move. Every one of those reads
// is asked by nobody and says so (`Zenith-Unattended`), and a page that opens
// while one is out joins it rather than asking the cell twice.
//
// **Who sees it.** A caller who may read the work list, which is
// `network.write` at the site. Anybody else is shown nothing: no word, no
// link, and no read.
//
// **When it is drawn** (#1826, #2244). Only while there is something to say:
// the work list names a change, or whether it does is not known (*not read*,
// *not derived*, *not asked*). An answered empty list draws nothing, at
// Gabriel's request of 2026-10-16 (*"nothing to do, don't show nothing..."*),
// so the control appearing is itself the signal. Neither does a list whose
// only entries are plans that could not be made, and with changes the tooltip
// does not name them, at his ruling of 2026-10-31 (*"I don't understand why we
// are seeing this message if nothing is in planning. Make it quiet."*): a plan
// not made is no change waiting in Design, and it is the work list's to say,
// with which plan and why, and not the shell's. Nothing is drawn before the first
// answer either, and a re-read keeps the last answer on screen until the next
// one lands, so a site with changes does not blink on every move. An approval
// being followed draws its panel whatever the list says, and alone when the
// list is empty.
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { UNATTENDED } from "../api/hooks";
import { useWriteEpoch } from "../api/writes";
import { can } from "../authz";
import { plural } from "../dashboard/format";
import { convergenceWord, runningOf, State, useConvergence } from "../pages/devices/DevicesTable";
import { changesToApplyOf, outcomeOf, WORK_LIST_PATH, type WorkListRead } from "../pages/devices/family";
import { useWorkListRead } from "../pages/devices/FamilyTables";
import { firstBy } from "../pages/devices/firstBy";
import { reachedAny, settledOf, stopFollowing, useFollow, type Follow } from "./following";
import { ObjectLink } from "./ObjectLink";
import { ScopeLink, useScope } from "./scope";
import { useSession } from "./session";

/** Whether the changes half reads at all: at a site, for a caller holding
 * `network.write` there. Anybody else is shown nothing and nothing is read. */
export function useChangesSite(): string | undefined {
  const { me } = useSession();
  const { scope } = useScope();
  const siteId = scope.siteId;
  if (!siteId) return undefined;
  return can(me, "network.write", { org_id: scope.orgId, customer_id: scope.customerId, site_id: siteId }) ? siteId : undefined;
}

/** Calls `fn` when `key` changes, and not when it is first seen: the read the
 * hook makes on its own when the page opens is the first one. */
export function useOnChange(key: string, fn: () => void) {
  const last = useRef(key);
  useEffect(() => {
    if (last.current === key) return;
    last.current = key;
    fn();
  }, [key, fn]);
}

/** A navigation or one of the operator's own writes, as one key. */
export function useMoved(): string {
  const { pathname } = useLocation();
  const epoch = useWriteEpoch();
  return `${pathname}\u0000${epoch}`;
}

/** The changes half's state at one site: the work list's words, whether they
 * are drawn, and the approval being followed. Its reads are the ones #1811 and
 * #1826 rule: once when the page opens at the site, again on a move and on the
 * operator's own write, never on a timer, every one unasked. The caller keys
 * whatever holds this by the site, so a new site starts with no last answer. */
export function useChanges(siteId: string) {
  const work = useWorkListRead(siteId, true, { unattended: true });
  const moved = useMoved();
  const { reload } = work;
  useOnChange(moved, useCallback(() => reload(UNATTENDED), [reload]));
  const words = changesToApplyOf(useLastAnswer(work));
  const f = useFollow(siteId);
  const following = useFollowing(siteId, f);
  // ZENITH.md §18, as #1826 and #2244 amend it: an answered empty list draws nothing,
  // and neither does one whose only entries are plans not made, which the work list says; every
  // answer that is not known (not read, not derived, not asked) is drawn and says so, so a hidden
  // half means no change in Design that a plan found, and never a read that failed.
  const drawn = !!words && !words.quiet;
  return { siteId, words: drawn ? words : undefined, following };
}

export type Changes = ReturnType<typeof useChanges>;

/** The changes half's words in the top bar: the count, or what is not known. */
export function ChangesWord({ words }: { words: NonNullable<Changes["words"]> }) {
  return (
    <span className="shell-changes-word" data-state={words.count ? "changes" : words.word}>
      <span>Changes to apply</span>
      {words.count ? <span className="shell-changes-count">{words.count}</span> : <State words={words} />}
    </span>
  );
}

/** The changes half's part of the panel: the way to Design › Changes, where
 * each plan is read before the one confirmation approves it. */
export function ChangesSection({ words }: { words: NonNullable<Changes["words"]> }) {
  return (
    <div className="shell-activity-part">
      <ScopeLink to={WORK_LIST_PATH} className="shell-changes-link" data-state={words.count ? "changes" : words.word}
        title={words.title}>
        <span>Changes to apply</span>
        {words.count
          ? <span className="shell-changes-count">{words.count}</span>
          : <State words={words} />}
      </ScopeLink>
    </div>
  );
}

/** The work list's last answer, kept through a re-read. `useWorkListRead`
 * reads `loading` with no list while a reload is out, and the shell re-reads
 * on every move, so drawing that would take a site's changes off screen for
 * the length of each read. Before the first answer it is `loading`, which
 * draws nothing. The hook is left as it is for its other readers. */
function useLastAnswer(work: WorkListRead): WorkListRead {
  const answer = (w?: WorkListRead) => w?.kind === "ready" ? w.list : w?.kind === "error" ? w.problem : undefined;
  const [last, setLast] = useState<WorkListRead>();
  const now = answer(work);
  // Adjusting state while rendering, React's form for "information from previous renders":
  // the guard compares the answer's identity, which holds across renders, so it settles.
  if (now !== undefined && now !== answer(last)) setLast(work);
  return work.kind === "loading" && last ? last : work;
}

/** What an approval sent, then what the devices it reached report, until
 * every one of them has settled: its convergence read and its one line. The
 * read is asked only once the approval reached a device, again on a move until
 * every device has settled, and unasked. */
function useFollowing(siteId: string, f: Follow | undefined) {
  const any = !!f && reachedAny(f);
  const convergence = useConvergence(any ? siteId : undefined, { unattended: true });
  const data = convergence.data;
  const s = f && data ? settledOf(f, data) : undefined;
  const done = !any || !!s?.done;
  const moved = useMoved();
  const { reload } = convergence;
  useOnChange(moved, useCallback(() => { if (!done) reload(UNATTENDED); }, [done, reload]));
  if (!f) return undefined;

  // Restates ADR-0022 decision 3, ADR-0074 decision 1, ADR-0093 and ADR-0099 — a device runs
  // what was applied only when it reads `synchronized`; `unknown` is never shown as synchronized;
  // so the summary counts devices that settled, never devices that took the configuration.
  const summary = !any
    ? `Refused: ${f.entries.length} of ${f.entries.length} ${plural(f.entries.length, "plan")}`
    : s
      ? done
        ? `Settled: ${s.serials.length} ${plural(s.serials.length, "device")}`
        : `Following: ${s.settled} of ${s.serials.length} ${plural(s.serials.length, "device")} settled`
      : convergence.status === "error" ? "Following: not read" : "Following what was applied";
  return { f, any, convergence, s, done, summary };
}

/** The approval being followed, as one part of the panel. */
export function FollowingSection({ siteId, following }: { siteId: string; following: NonNullable<Changes["following"]> }) {
  const { f, any, convergence, s, done, summary } = following;
  const data = convergence.data;
  const states = new Map<string, number>();
  const notCurrent: string[] = [];
  if (s && data) {
    const row = firstBy(data.devices, "serial");
    for (const serial of s.serials) {
      const d = row.get(serial);
      const label = d ? convergenceWord(d).label : "not reported";
      states.set(label, (states.get(label) ?? 0) + 1);
      if (!d || d.state !== "synchronized") notCurrent.push(serial);
    }
  }
  return (
    <div className="shell-activity-part shell-following" data-done={done ? "true" : "false"}>
      <p className="shell-activity-head">{summary}</p>
      <p className="small"><strong>What the approval sent</strong></p>
      <ul className="shell-following-list">
        {f.entries.map(({ outcome, name }) => (
          <li key={outcome.target} data-outcome={outcome.outcome}>{name}: <State words={outcomeOf(outcome)} /></li>
        ))}
      </ul>
      {any && <>
        <p className="small"><strong>What the devices report</strong></p>
        {convergence.status === "error" && <p className="small">The site&rsquo;s convergence could not be read: {convergence.problem.message ?? convergence.problem.reason}.</p>}
        {data?.reason === "placement" && <p className="small">The site&rsquo;s placement does not resolve, so no device could be judged.</p>}
        {s && s.serials.length === 0 && <p className="small">The approval reached no device this site&rsquo;s convergence names.</p>}
        {states.size > 0 && <p className="small" data-states>{[...states].map(([label, n]) => `${label} ${n}`).join(" · ")}</p>}
        {notCurrent.length > 0 && (
          <ul className="shell-following-list">
            {notCurrent.map((serial) => {
              const w = runningOf(serial, convergence);
              return <li key={serial}><ObjectLink kind="device" value={serial}>{serial}</ObjectLink>{" "}{w ? <State words={w} /> : <span className="muted">not reported</span>}</li>;
            })}
          </ul>
        )}
        {/* Restates ADR-0022 decision 3 and ADR-0093 — `behind` and `unknown` are not a device
            running what was applied, so a settled approval is not a synchronized one. */}
        <p className="hint">
          {done
            ? "Every device the approval reached has settled. A device that is behind or unknown is not running what was applied."
            : "This is read again each time you move to another page, until every device has settled. A device that is syncing, offline or carried has not settled."}
        </p>
      </>}
      <div className="actions">
        {any && !done && <button type="button" className="btn" onClick={() => convergence.reload()}>Read again</button>}
        <button type="button" className="btn" onClick={() => stopFollowing(siteId)}>{done || !any ? "Dismiss" : "Stop following"}</button>
      </div>
    </div>
  );
}
